import { Component, EventEmitter, Output, Input, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UploadService } from '../../../../core/services/upload.service';
import { AlertService } from '../../../../shared/services/alert.service';

interface UploadedImage {
  id: number | string;
  url: string;
  progress: number;
  error?: string;
}

@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="image-upload-container">
      <div class="upload-zone" (click)="fileInput.click()" (dragover)="onDragOver($event)" (drop)="onDrop($event)">
        <input type="file" #fileInput (change)="onFileSelected($event)" multiple accept="image/*" style="display: none">
        <div class="upload-placeholder">
          <i class="fas fa-images"></i>
          <span>Şəkil əlavə etmək</span>
          <p class="hint">Minimum 4, maksimum 20 şəkil. İlk şəkil əsas şəkil olacaq.</p>
        </div>
      </div>

      <div class="images-grid" *ngIf="images().length > 0">
        <div class="image-card" *ngFor="let img of images(); let i = index" [class.main]="i === 0" [class.error]="img.error">
          <ng-container *ngIf="img.progress === 100; else uploadingState">
            <img [src]="img.url" alt="Listing image">
          </ng-container>

          <ng-template #uploadingState>
            <div class="loader-container" *ngIf="!img.error">
              <i class="fas fa-spinner fa-spin"></i>
              <span *ngIf="img.progress < 100">{{img.progress}}%</span>
            </div>
            <div class="error-container" *ngIf="img.error">
              <i class="fas fa-exclamation-circle"></i>
              <span class="error-msg">{{img.error}}</span>
            </div>
          </ng-template>

          <div class="progress-bar" *ngIf="img.progress < 100 && !img.error">
            <div class="progress" [style.width.%]="img.progress"></div>
          </div>
          <button class="remove-btn" (click)="removeImage(img.id)">
            <i class="fas fa-times"></i>
          </button>
          <div class="main-badge" *ngIf="i === 0 && img.progress === 100">Əsas</div>
          <div class="reorder-btns" *ngIf="img.progress === 100">
            <button *ngIf="i > 0" (click)="move(i, -1)"><i class="fas fa-chevron-left"></i></button>
            <button *ngIf="i < images().length - 1" (click)="move(i, 1)"><i class="fas fa-chevron-right"></i></button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .image-upload-container { display: flex; flex-direction: column; gap: 20px; }
    .upload-zone {
      border: 2px dashed #e2e8f0; border-radius: 20px; padding: 48px 24px; text-align: center;
      cursor: pointer; transition: all 0.2s; background: #f8fafc;
      &:hover { border-color: #3b82f6; background: #f0f7ff; }
    }
    .upload-placeholder {
      display: flex; flex-direction: column; align-items: center; gap: 16px;
      i { font-size: 48px; color: #cbd5e1; }
      span { font-size: 18px; font-weight: 700; color: #1e293b; }
      .hint { font-size: 14px; color: #64748b; }
    }
    .images-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 16px;
    }
    .image-card {
      position: relative; aspect-ratio: 4/3; border-radius: 12px; overflow: hidden;
      border: 1px solid #e2e8f0; background: #f1f5f9;
      img { width: 100%; height: 100%; object-fit: cover; }
      &.main { border: 2px solid #3b82f6; grid-column: span 2; grid-row: span 2; }
      &.error { border-color: #ef4444; background: #fef2f2; }
    }
    .loader-container, .error-container {
      width: 100%; height: 100%; display: flex; flex-direction: column;
      align-items: center; justify-content: center; gap: 8px; padding: 10px;
      i { font-size: 24px; color: #3b82f6; }
    }
    .error-container {
      i { color: #ef4444; }
      .error-msg { font-size: 11px; color: #ef4444; text-align: center; font-weight: 500; }
    }
    .remove-btn {
      position: absolute; top: 8px; right: 8px; background: rgba(255,255,255,0.9);
      border: none; border-radius: 50%; width: 28px; height: 28px; cursor: pointer;
      color: #ef4444; display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1); z-index: 2;
    }
    .main-badge {
      position: absolute; bottom: 8px; left: 8px; background: #3b82f6; color: #fff;
      padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;
    }
    .progress-bar {
      position: absolute; bottom: 0; left: 0; width: 100%; height: 4px; background: rgba(0,0,0,0.1);
      .progress { height: 100%; background: #3b82f6; transition: width 0.3s; }
    }
    .reorder-btns {
      position: absolute; bottom: 8px; right: 8px; display: flex; gap: 4px;
      button { background: rgba(255,255,255,0.9); border: none; border-radius: 4px; padding: 4px; cursor: pointer; }
    }
  `]
})
export class ImageUploadComponent {
  @Output() imagesChanged = new EventEmitter<number[]>();
  @Output() urlsChanged = new EventEmitter<string[]>();

  @Input() set initialImages(data: {id: any, url: string}[] | string[] | null | undefined) {
    if (data && data.length > 0 && this.images().length === 0) {
      const initial: UploadedImage[] = data.map((item, index) => {
        if (typeof item === 'string') {
          return { id: `existing-${index}`, url: item, progress: 100 };
        } else {
          return { id: item.id, url: item.url, progress: 100 };
        }
      });
      this.images.set(initial);
    }
  }

  images = signal<UploadedImage[]>([]);
  private alertService = inject(AlertService);

  constructor(private uploadService: UploadService) {}

  onFileSelected(event: any) {
    const files = event.target.files;
    this.uploadFiles(files);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files) this.uploadFiles(files);
  }

  private uploadFiles(files: FileList) {
    const currentCount = this.images().length;
    const remainingCount = 20 - currentCount;

    if (remainingCount <= 0) {
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingCount);

    filesToUpload.forEach(file => {
      const tempId = Math.random().toString(36).substring(7);
      // Şəkil URL-i hələ təyin edilmir, çünki API cavabından sonra göstərilməlidir
      const newImg: UploadedImage = { id: tempId, url: '', progress: 10 };
      this.images.update(prev => [...prev, newImg]);

      // Progress simulation (10% -> 30% -> 70%)
      setTimeout(() => {
        this.images.update(prev => prev.map(img =>
          img.id === tempId && img.progress === 10 ? { ...img, progress: 30 } : img
        ));
      }, 500);

      setTimeout(() => {
        this.images.update(prev => prev.map(img =>
          img.id === tempId && img.progress === 30 ? { ...img, progress: 70 } : img
        ));
      }, 1500);

      this.uploadService.uploadImage(file).subscribe({
        next: (res) => {
          this.images.update(prev => prev.map(img =>
            img.id === tempId ? { ...img, id: res.id, url: res.url, progress: 100 } : img
          ));
          this.notify();
        },
        error: (err) => {
          const errorKey = typeof err === 'string' ? err : (err.message || 'UNKNOWN_ERROR');
          const errorMessage = this.alertService.getErrorMessage(errorKey);
          this.images.update(prev => prev.map(img =>
            img.id === tempId ? { ...img, progress: 0, error: errorMessage } : img
          ));
        }
      });
    });
  }

  removeImage(id: number | string) {
    this.images.update(prev => prev.filter(img => img.id !== id));
    this.notify();
  }

  move(index: number, direction: number) {
    const newImages = [...this.images()];
    const temp = newImages[index];
    newImages[index] = newImages[index + direction];
    newImages[index + direction] = temp;
    this.images.set(newImages);
    this.notify();
  }

  private notify() {
    const uploaded = this.images().filter(img => img.progress === 100);
    const ids = uploaded.map(img => {
      if (typeof img.id === 'number') return img.id;
      const parsed = parseInt(img.id.toString().replace('existing-', ''));
      return isNaN(parsed) ? 0 : parsed;
    }).filter(id => id !== 0);

    this.imagesChanged.emit(ids);
    this.urlsChanged.emit(uploaded.map(img => img.url));
  }
}
